import {
  buildFilterString,
  buildQueryUrl,
  buildSearchString,
  fetchData,
} from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import { InstitutionPostBodyDTO, KeyValue, Query } from "../../interfaces";


export async function getBankFindApiFilters(appState: Query) {

  const locationFormOptions =
    appState.filters.stalp || appState.filters.zip || appState.search.city;
  if (locationFormOptions && appState.ui.branchOffices) {
    // if we have a location based search (state, zip, or city)
    // AND branch offices are to be included
    const locationObject = await getApiFiltersLocations(appState);
    return getApiUrlWithLocations(locationObject, appState);
  } else {
    // any search with no branch offices
    return buildQueryUrl(`/institutions`, appState);
  }
}

async function getApiFiltersLocations(appState: any) {
  const locationFilters = [];
  const locationSearch = [];

  //filter results
  if (appState.filters.zip) {
    locationFilters.push(`ZIP:"${appState.filters.zip}"`);
  }
  if (appState.filters.stalp) {
    locationFilters.push(`STALP:"${appState.filters.stalp}"`);
  }
  if (appState.filters.county) {
    locationFilters.push(`COUNTY:"${appState.filters.county}"`);
  }
  //search results
  if (appState.search.city) {
    locationSearch.push(`CITY:${appState.search.city}`);
  }

  const filterString = locationFilters
    .map((filter, idx) => {
      if (idx === 0) {
        return `&filters=(${filter})`;
      }
      return ` AND (${filter})`;
    })
    .join("");

  const searchString = locationSearch
    .map((search, idx) => {
      if (idx === 0) {
        return `&search=(${search})`;
      }
      return ` AND (${search})`;
    })
    .join("");

  const locationResults = await fetchData(
    `${ApiBaseUrl()}/locations?agg_by=CERT&agg_limit=10000${filterString}${searchString}`,
  );

  //gets array of certs from locations API call
  const locationCertArray = locationResults.data.map(
    (record: KeyValue) => record.CERT,
  );

  let institutionsCertArray = [];

  if (appState.search.city) {
    const institutionsResults = await fetchData(
      `${ApiBaseUrl()}/institutions?&limit=10000&offset=0${filterString}${searchString}`,
    );
    //gets array of certs from institutions API call
    institutionsCertArray = institutionsResults.data.map(
      (record: KeyValue) => record.CERT,
    );
  }

  //combines both arrays and then filters out any duplicates
  const combinedArray = locationCertArray.concat(institutionsCertArray);
  const filteredArrays = combinedArray.filter((value: string, idx: number) => {
    return combinedArray.indexOf(value) === idx;
  });

  return {
    certs: filteredArrays,
    matchingCert: filteredArrays.includes(appState.filters.cert),
  };
}

function getApiUrlWithLocations(locationObject: KeyValue, appState: Query) {
  const updatedAppState = locationObject.matchingCert
    ? {
      ...appState,
      filters: { cert: appState.filters.cert },
      search: { ...appState.search, city: "" },
    }
    : {
      ...appState,
      filters: {
        ...appState.filters,
        multiCert: appState.filters.cert ? [] : locationObject.certs,
        stalp:
          locationObject.certs.length > 0 && appState.search.city
            ? ""
            : appState.filters.stalp,
        zip:
          locationObject.certs.length > 0 && appState.search.city
            ? ""
            : appState.filters.zip,
      },
      search: {
        ...appState.search,
        city: locationObject.certs.length > 0 ? "" : appState.search.city,
      },
    };
  const removeActiveFilter = (filters: any) => {
    //replaces starting active/inactive values that will override location search
    let formattedFilters = filters.startsWith('((ACTIVE:"0"))')
      ? filters.replace('((ACTIVE:"0")) OR ', "")
      : filters.startsWith('((ACTIVE:"1"))')
        ? filters.replace('((ACTIVE:"1")) OR ', "")
        : filters.startsWith('((ACTIVE:"0" OR ACTIVE:"1"))')
          ? filters.replace('((ACTIVE:"0" OR ACTIVE:"1")) OR ', "")
          : filters;

    //properly formats the values for active and inactive
    formattedFilters = formattedFilters.includes('ACTIVE:"0,1"')
      ? formattedFilters.replace('ACTIVE:"0,1"', '(ACTIVE:"0" OR ACTIVE:"1")') : formattedFilters;

    return formattedFilters;
  };


  const requestBody: InstitutionPostBodyDTO = {
    filters: removeActiveFilter(buildFilterString(updatedAppState.filters)),
    search: buildSearchString(updatedAppState.search),
    sort_by: updatedAppState.sort.sortField,
    sort_order: updatedAppState.sort.sortOrder,
    offset:
      (appState.pagination.pageNumber - 1) * appState.pagination.resultLimit,

    limit: appState.pagination.resultLimit,
    fields: "",
    agg_by: appState.agg.agg_by,
    agg_term_fields: appState.agg.agg_term_fields,
  };

  return requestBody;
}
